import axios from 'axios'
import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import qs from 'qs'
import { Message } from 'element-ui'
import 'element-ui/lib/theme-chalk/message.css'

// import { env } from '@/env'

import { ERROR_CODES, SUCCESS_CODE } from './consts'
import type { IApiResponse } from './types'

const getHeaders = () => {
  // const _env = env.VITE_MODE
  let headers = {}
  // if (_env === 'pre') {
  //   headers = {
  //     'PREVIEW-USER': 1,
  //   }
  // }
  return headers
}
// 开发环境下使用相对路径，让请求通过代理；生产环境使用完整 URL
const baseURL = process.env.NODE_ENV === 'development' 
  ? '' 
  : process.env.VUE_APP_BASE_URL;
const axiosInstance = axios.create({
  baseURL,
  timeout: 60 * 1000, // 60秒
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'x-debug': true,
    ...getHeaders(),
  },
})

// 请求拦截处理
axiosInstance.interceptors.request.use(
  (config) => updateRequestConfig(config),
  (error) => Promise.reject(error)
)

// 响应拦截处理
axiosInstance.interceptors.response.use(
  (response) => checkResponseData(response), // 处理 response.status 为 2xx 的情况
  (error) => handleResponseError(error) // 处理 response.status 不为 2xx 的错误
)

function updateRequestConfig(config: InternalAxiosRequestConfig) {
  updateData(config)

  return config
}

// todo
function updateData(config: InternalAxiosRequestConfig) {
  // 如果 data 不是 FormData，但发送的数据需要是 x-www-form-urlencoded 类型，则需要将 data 转换为标准的数据格式
  if (
    Object.prototype.toString.call(config.data) !== '[object FormData]' &&
    config.headers['Content-Type'] === 'application/x-www-form-urlencoded;charset=UTF-8'
  ) {
    config.data = qs.stringify(config.data)
  }
}

/**
 * 请求正常服务端返回 code 处理
 * 接口需分 upc、相应后端服务 两种服务接口，UPC接口字符串含v1，通过v1 区别
 * 能进到这个 function，说明 response.status 为 2xx，此时根据 response code & data 进行错误处理
 * @param {*} response
 */
function checkResponseData(response: AxiosResponse) {
  // 有响应数据，且 code ok，则 resolve 接口返回的所有内容，业务代码中直接使用 response.data 即可，无需再次判断 code
  if (response.data?.code === SUCCESS_CODE) {
    return response
  }

  checkLogin(response)
  if (!response?.config?.handleError) {
    showErrorMessage(response)
  }

  return Promise.reject(response)
}

function checkLogin(response: AxiosResponse) {
  if (
    response.config.url?.match('/nr-qiankun-center/') &&
    response.data.code === ERROR_CODES.LOGIN_REQUIRED
  ) {
    // TODO 无权限跳转
    // window.location.href = `${
    //   env.VITE_UPC_HOSTNAME || UPC_HOSTNAME_TESTING
    // }/login?redirect=${encodeURIComponent(window.location.href)}`
  }
}

/**
 * @description 自定义逻辑封装：后端子服务接口code处理
 */
function showErrorMessage(response: AxiosResponse) {
  const { code, message, traceId, msg } = response.data

  const errorMessage = msg || message || ''
  
  // 构建显示的错误信息
  let displayMessage = errorMessage || `后端接口${code}异常`
  
  // 如果有 traceId，在开发环境下显示
  if (process.env.NODE_ENV === 'development' && traceId) {
    displayMessage = `${displayMessage}[${traceId}]`
  }
  
  // 使用 Element UI 的 Message 组件显示错误信息
  if (displayMessage) {
    Message.error(displayMessage)
  }
}

/**
 * 请求异常 status 处理
 * @param {*} res
 */
function handleResponseError(error: AxiosError) {
  // 如果是取消请求的错误，不显示错误提示，直接 reject
  if (
    error.code === 'ERR_CANCELED' ||
    error.message === 'canceled' ||
    error.name === 'CanceledError' ||
    error.name === 'AbortError'
  ) {
    return Promise.reject(error)
  }

  let errorMessage = error.message || '未知错误'

  if (errorMessage === 'Network Error') {
    errorMessage = '后端接口连接异常'
  }
  if (errorMessage.includes('timeout')) {
    errorMessage = '后端接口请求超时'
  }
  if (errorMessage.includes('Request failed with status code')) {
    const codeMatch = errorMessage.match(/code (\d+)/)
    const code = codeMatch ? codeMatch[1] : 'unknown'
    errorMessage = `后端接口${code}异常`
  }

  // 使用 Element UI 的 Message 组件显示错误信息
  Message.error(errorMessage)

  return Promise.reject(error)
}

export const apiClient = {
  // 抛出axios 实例方便扩展
  request: axiosInstance,

  /** GET 请求，返回接口响应数据 */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get: async <T = any>(url: string, config?: AxiosRequestConfig) => {
    const response = await axiosInstance.get<IApiResponse<T>>(url, config)
    return response.data
  },

  /** GET 请求，返回包含完整 Axios Response 的响应对象（无特殊需求，直接使用 apiClient.get() 即可） */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getWithResponse: <T = any>(url: string, config?: AxiosRequestConfig) => {
    return axiosInstance.get<T>(url, config)
  },

  /** POST 请求，返回接口响应数据 */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post: async <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => {
    const response = await axiosInstance.post<IApiResponse<T>>(url, data, config)
    return response.data
  },

  /** POST 请求，返回包含完整 Axios Response 的响应对象（无特殊需求，直接使用 apiClient.post() 即可） */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  postWithResponse: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => {
    return axiosInstance.post<T>(url, data, config)
  },
}
